import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase, SanPhamHanQuoc } from "../../../lib/supabase";
import { createSlug } from "../../../lib/slug";
import ProductFormModal from "./ProductFormModal";

type Lang = "vi" | "kr";

interface Props {
  lang: Lang;
}

const ProductsManager: React.FC<Props> = ({ lang }) => {
  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);
  const [products, setProducts] = useState<SanPhamHanQuoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState<SanPhamHanQuoc | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SanPhamHanQuoc | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState("");

  const fetchProducts = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("san_pham_han_quoc")
      .select("*")
      .order("created_at", { ascending: false });
    setProducts(data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await supabase.from("san_pham_han_quoc").delete().eq("id", deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
    showToast(t("Đã xóa sản phẩm!", "제품이 삭제되었습니다!"));
    fetchProducts();
  };

  const filtered = products.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.company_name ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (p.category_name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-[60] bg-green-600 text-white text-sm px-4 py-2.5 rounded-xl">
          <i className="ri-checkbox-circle-line mr-2" />{toast}
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">{t("Quản Lý Sản Phẩm", "제품 관리")}</h1>
        <p className="text-sm text-gray-500 mt-1">{t("Thêm, sửa, xóa sản phẩm trong bảng Supabase", "Supabase 테이블에서 제품 추가·수정·삭제")}</p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-80">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("Tìm theo tên, công ty...", "제품명, 회사로 검색...")}
            className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:border-[#004A99] outline-none w-full"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1.5 rounded-lg whitespace-nowrap">
            {filtered.length} {t("sản phẩm", "개 제품")}
          </span>
          <button
            onClick={() => { setEditProduct(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            {t("Thêm sản phẩm", "제품 추가")}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center">
            <i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin block mb-2" />
            <p className="text-sm text-gray-400">{t("Đang tải...", "로딩 중...")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#F8F9FA] border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">{t("Tên sản phẩm", "제품명")}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">{t("Công ty", "회사")}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">{t("Danh mục", "카테고리")}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">{t("Nổi bật", "추천")}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">{t("Hành động", "작업")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-[#F8F9FA] transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-800 whitespace-nowrap max-w-48 truncate">{p.name}</p>
                      {p.name_kr && <p className="text-gray-400 text-[10px] whitespace-nowrap">{p.name_kr}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap max-w-36 truncate">{p.company_name ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] px-2 py-0.5 bg-[#EDF2FB] text-[#004A99] rounded-full font-semibold whitespace-nowrap">
                        {p.category_name ?? "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {p.is_featured ? (
                        <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-semibold">
                          ★ {t("Nổi bật", "추천")}
                        </span>
                      ) : (
                        <span className="text-[10px] text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/${createSlug(p.company_name ?? "unknown")}/${createSlug(p.name)}`}
                          target="_blank"
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#EDF2FB] text-[#004A99] transition-colors cursor-pointer"
                          title={t("Xem trang", "페이지 보기")}
                        >
                          <i className="ri-external-link-line text-sm" />
                        </Link>
                        <button
                          onClick={() => { setEditProduct(p); setShowModal(true); }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-green-50 text-green-600 transition-colors cursor-pointer"
                          title={t("Chỉnh sửa", "수정")}
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(p)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-500 transition-colors cursor-pointer"
                          title={t("Xóa", "삭제")}
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-12 text-center text-gray-400 text-sm">
                <i className="ri-inbox-line text-2xl block mb-2" />
                {t("Chưa có sản phẩm nào. Hãy thêm sản phẩm đầu tiên!", "아직 제품이 없습니다. 첫 번째 제품을 추가해 보세요!")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Product Form Modal */}
      {showModal && (
        <ProductFormModal
          lang={lang}
          editData={editProduct}
          onClose={() => { setShowModal(false); setEditProduct(null); }}
          onSuccess={() => {
            setShowModal(false);
            setEditProduct(null);
            showToast(editProduct ? t("Đã cập nhật sản phẩm!", "제품이 수정되었습니다!") : t("Đã thêm sản phẩm mới!", "새 제품이 추가되었습니다!"));
            fetchProducts();
          }}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-delete-bin-line text-red-600 text-xl" />
            </div>
            <h3 className="font-bold text-gray-800 mb-2">{t("Xác nhận xóa", "삭제 확인")}</h3>
            <p className="text-sm text-gray-600 mb-5">
              {t("Bạn có chắc muốn xóa sản phẩm", "다음 제품을 삭제하시겠습니까?")}
              {" "}<strong>"{deleteTarget.name}"</strong>?{" "}
              {t("Hành động này không thể hoàn tác.", "이 작업은 취소할 수 없습니다.")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                {t("Hủy", "취소")}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1"
              >
                {deleting && <i className="ri-loader-4-line animate-spin" />}
                {t("Xóa", "삭제")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsManager;
